import React, { useState } from 'react';
import { useOnboardingDataRedux } from '../hooks/useOnboardingDataRedux';

const OnboardingDataDebug: React.FC = () => {
  const { data, getCompletionPercentage, resetData } = useOnboardingDataRedux();
  const [isVisible, setIsVisible] = useState(false);

  if (process.env.NODE_ENV === 'production') return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        Debug Data ({getCompletionPercentage()}%)
      </button>
      
      {isVisible && (
        <div className="absolute bottom-12 right-0 w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-4 max-h-96 overflow-y-auto">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-900">Onboarding Data</h3>
            <button
              onClick={resetData}
              className="text-red-600 hover:text-red-700 text-xs font-medium"
            >
              Reset
            </button>
          </div>
          
          <div className="space-y-3 text-xs">
            {/* Progress */}
            <div>
              <div className="font-medium text-gray-700 mb-1">Progress</div>
              <div className="bg-gray-200 rounded-full h-2 mb-1">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
              </div>
              <div className="text-gray-500">
                Step {data.currentStep} of 4 ({getCompletionPercentage()}% complete)
              </div>
            </div>

            {/* Step 1: Signup Data */}
            <div className="border-t pt-2">
              <div className="font-medium text-gray-700 mb-1">
                Step 1: Signup {data.completedSteps[0] ? '✅' : '⏳'}
              </div>
              <div className="text-gray-600 space-y-1">
                <div>Email: {data.email || 'Not set'}</div>
                <div>Policy: {data.acceptedPolicy ? 'Accepted' : 'Not accepted'}</div>
                <div>Clerk ID: {data.clerkUserId ? 'Set' : 'Not set'}</div>
              </div>
            </div>

            {/* Step 2: Pricing Data */}
            <div className="border-t pt-2">
              <div className="font-medium text-gray-700 mb-1">
                Step 2: Pricing {data.completedSteps[1] ? '✅' : '⏳'}
              </div>
              <div className="text-gray-600 space-y-1">
                <div>Plan: {data.selectedPlan || 'Not selected'}</div>
                <div>Price: ${data.planPrice || 'Not set'}</div>
                <div>Features: {data.planFeatures?.length || 0} items</div>
              </div>
            </div>

            {/* Step 3: Phone Verification Data */}
            <div className="border-t pt-2">
              <div className="font-medium text-gray-700 mb-1">
                Step 3: Phone Verification {data.completedSteps[2] ? '✅' : '⏳'}
              </div>
              <div className="text-gray-600 space-y-1">
                <div>Phone: {data.phoneNumber || 'Not set'}</div>
                <div>SMS Consent: {data.smsConsent ? 'Yes' : 'No'}</div>
              </div>
            </div>

            {/* Step 4: Payment & Account Creation Data */}
            <div className="border-t pt-2">
              <div className="font-medium text-gray-700 mb-1">
                Step 4: Payment & Account {data.completedSteps[3] ? '✅' : '⏳'}
              </div>
              <div className="text-gray-600 space-y-1">
                <div>Method: {data.paymentMethod || 'Not set'}</div>
                <div>Status: {data.paymentStatus || 'Pending'}</div>
                <div>Stripe ID: {data.stripePaymentIntentId ? 'Set' : 'Not set'}</div>
                <div>Profile: {data.profileCreated ? 'Created' : 'Not created'}</div>
                <div>Paid User: {data.paidUserDataAdded ? 'Added' : 'Not added'}</div>
              </div>
            </div>

            {/* Metadata */}
            <div className="border-t pt-2">
              <div className="font-medium text-gray-700 mb-1">Metadata</div>
              <div className="text-gray-600 space-y-1">
                <div>Started: {new Date(data.startedAt).toLocaleString()}</div>
                {data.completedAt && (
                  <div>Completed: {new Date(data.completedAt).toLocaleString()}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingDataDebug; 